import { QueryClient, QueryObserverOptions } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"

import { ResponseConfig, ResponseErrorConfig } from "~/backend/axiosClient"
import { usePagination } from "~/components/hooks/usePagination"

type Config<TData> = {
  query?: Partial<
    QueryObserverOptions<
      ResponseConfig<TData>,
      ResponseErrorConfig<Error>,
      TData,
      ResponseConfig<TData>
    >
  > & { client?: QueryClient }
}

type UsePaginatedQueryReturn<TData> = {
  data: TData
  isPending: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  totalCount: number
  totalPages: number
  pageBasedPagination: ReturnType<typeof usePagination>["pageBasedPagination"]
  cursorBasedPagination: ReturnType<
    typeof usePagination
  >["cursorBasedPagination"]
  setPagination: ReturnType<typeof usePagination>["setPagination"]
  setPageBasedPagination: ReturnType<
    typeof usePagination
  >["setPageBasedPagination"]
  goToFirstPage: ReturnType<typeof usePagination>["goToFirstPage"]
}

export function usePaginatedQuery<
  T extends (args: any, config: any) => any,
  TData,
>(
  fetchHook: T,
  args: Omit<Parameters<T>[0], "pagination"> = {} as any,
  config: Config<TData> = {}
): UsePaginatedQueryReturn<TData> {
  const {
    pagination,
    setPagination,
    setPageBasedPagination,
    pageBasedPagination,
    cursorBasedPagination,
    goToFirstPage,
  } = usePagination()

  const {
    data: responseData,
    isPending,
    isFetching,
    isError,
    error,
    ...rest
  } = fetchHook(
    {
      ...args,
      pagination,
    },
    config
  )

  const data = responseData?.data

  const totalCount = useMemo(() => {
    return data?.count || 0
  }, [data?.count])

  const totalPages = useMemo(() => {
    return Math.max(
      1,
      Math.ceil((totalCount || 0) / pageBasedPagination.pageSize)
    )
  }, [totalCount, pageBasedPagination.pageSize])

  useEffect(() => {
    goToFirstPage()
  }, [args.filters, args.sorts, goToFirstPage])

  return {
    data,
    isPending,
    isFetching,
    isError,
    error,
    pageBasedPagination,
    cursorBasedPagination,
    totalCount,
    totalPages,
    pagination,
    setPagination,
    setPageBasedPagination,
    goToFirstPage,
    ...rest,
  }
}
